import cn from 'clsx';
import { useUI } from '@v2/shared/lib/ui';
import { Category } from '@v2/shared/lib/types';
import { useSite } from '@v2/shared/lib/site';
import { useCart } from '@v2/shared/lib/cart';
import { useUser } from '@v2/shared/lib/user';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import useToggle from '@v2/shared/lib/useToggle';
import useMenuItems from './useMenuItems';
import useShowFixedHeader from '@v2/shared/lib/useShowFixedHeader';
import useSearchAutoComplete, { SearchCallback } from '@v2/shared/lib/useSearchAutoComplete';

import MobileNav from './MobileNav';
import DesktopNav from './DesktopNav';
import AccountDialog from './AccountDialog';

import '@v2/shared/lib/fontAwesome';

type Props = {
  categories: Category[];
  featuredCategories: Category[];
  booksCategories?: Category[];
  featuredBooksCategories?: Category[];
  searchCallback: SearchCallback;
};

function Navigation({
  categories,
  featuredCategories,
  booksCategories,
  featuredBooksCategories,
  searchCallback,
}: Props) {
  const { country } = useSite();
  // The count is known only once the cart request has answered, and until then the badge
  // is left out: a 0 that turns into a 2 is a flicker the visitor sees on every stateless page.
  const { cartCount: loadedCartCount, loaded } = useCart();
  const { user } = useUser();
  const cartCount = loaded ? loadedCartCount : null;
  const { buildUrl } = useUrlGenerator();
  const homeUrl = buildUrl('/');
  const cartUrl = buildUrl('/cart');
  const { headerRef, headerHeight, searchBar: searchBarState } = useUI();
  const showFixedHeader = useShowFixedHeader(headerHeight);
  const { specialDealLinks, socialLinks, accountLinks } = useMenuItems();
  const accountMenuState = useToggle();
  const searchAutoCompleteProps = useSearchAutoComplete(searchCallback);

  return (
    <>
      {/*
        Reserves the height of the fixed header, which is out of flow and so reserves
        none of its own.

        headerHeight is measured after mount, and useResizeObserver reports nothing on
        the first render, so this used to render `height: 0px`: the first painted frame
        reserved no space at all, content sat under the header, and everything jumped
        down once the measurement arrived. The classes below reserve the right space
        before javascript has run, and the inline value takes over once it has.

        The three values are the three states the navigation has -- mobile, the tablet
        layout that wraps the menu onto a second row, and the single-row desktop one --
        measured at 109px, 126px and 82px. Note the middle one is the tallest; treating
        this as small-to-large gets the tablet case wrong by 44px.

        They are a first-frame estimate and nothing more. Whatever the header actually
        measures replaces them a moment later, so content drift costs a small correction
        rather than a broken layout, and no width is left unreserved.

        min-height rather than height, deliberately: postcss/beatLegacyImportant.js
        raises `height` to !important, which would make these classes outrank the inline
        measurement and pin the spacer to the estimate for good. `min-height` is not on
        that list. The div is empty, so its min-height is its height.
      */}
      <div
        className="min-h-[109px] md:min-h-[126px] xl:min-h-[82px]"
        style={headerHeight ? { minHeight: `${headerHeight}px` } : undefined}
      ></div>
      <header ref={headerRef} className={cn('block top-0 inset-x-0 fixed z-[250]')}>
        <MobileNav
          categories={categories}
          featuredCategories={featuredCategories}
          booksCategories={booksCategories}
          featuredBooksCategories={featuredBooksCategories}
          accountMenuState={accountMenuState}
          searchBarState={searchBarState}
          fixedHeaderVisible={showFixedHeader}
          cartUrl={cartUrl}
          homeUrl={homeUrl}
          cartCount={cartCount}
          specialDealLinks={specialDealLinks}
          socialLinks={socialLinks}
          searchAutoCompleteProps={searchAutoCompleteProps}
        />
        <DesktopNav
          country={country}
          categories={categories}
          booksCategories={booksCategories}
          user={user}
          searchBarState={searchBarState}
          specialDealLinks={specialDealLinks}
          accountMenuState={accountMenuState}
          cartCount={cartCount}
          cartUrl={cartUrl}
          homeUrl={homeUrl}
          searchAutoCompleteProps={searchAutoCompleteProps}
        />
        <AccountDialog
          isOpen={accountMenuState.isOpen}
          toggle={accountMenuState.toggle}
          close={accountMenuState.close}
          accountLinks={accountLinks}
          user={user}
        />
      </header>
    </>
  );
}

export default Navigation;
